// components/UserDetailsDrawer.tsx
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Table,
  Typography,
  Space,
  Tag,
  Spin,
  Alert,
  Row,
  Col,
  Pagination,
  Button,
  Badge
} from 'antd';
import {
  UserOutlined,
  DollarOutlined,
  CalendarOutlined,
  CloseOutlined,
  EyeOutlined,
  RiseOutlined,
  FallOutlined,
  ClockCircleOutlined,
  CrownOutlined,
  TrophyOutlined,
  DownOutlined,
  TeamOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { UserCategoryDetails } from '../../../types/analytics';
import { AnalyticsService } from '../../../services/analyticsService';

const { Text, Title } = Typography;

interface UserDetailsDrawerProps {
  visible: boolean;
  onClose: () => void;
  category: 'total' | 'usersWithDeposits' | 'whales' | 'dolphins' | 'fish' | 'inactive' | null;
  categoryData?: {
    count: number;
    totalValue?: number;
    percentage: number;
  };
  onLoadDetails: (category: string, limit: number, offset: number) => Promise<UserCategoryDetails>;
  theme?: 'dark' | 'light';
}

const getCategoryInfo = (category: string) => {
  const categoryMap = {
    total: {
      title: 'Total de Usuarios',
      description: 'Todos los usuarios registrados en el sistema',
      color: '#1890ff',
      gradient: 'linear-gradient(135deg, #69c0ff 0%, #1890ff 100%)',
      icon: TeamOutlined,
      level: 'Todos'
    },
    usersWithDeposits: {
      title: 'Usuarios con Depósitos',
      description: 'Usuarios que han realizado al menos un depósito',
      color: '#f5222d',
      gradient: 'linear-gradient(135deg, #ff7875 0%, #f5222d 100%)',
      icon: UserOutlined,
      level: 'Activos'
    },
    whales: {
      title: 'Ballenas',
      description: 'Elite - Usuarios con más de $500,000 en depósitos totales',
      color: '#722ed1',
      gradient: 'linear-gradient(135deg, #b37feb 0%, #722ed1 100%)',
      icon: CrownOutlined,
      level: 'Elite'
    },
    dolphins: {
      title: 'Delfines',
      description: 'Premium - Usuarios con $100,000 - $500,000 en depósitos',
      color: '#1890ff',
      gradient: 'linear-gradient(135deg, #69c0ff 0%, #1890ff 100%)',
      icon: TrophyOutlined,
      level: 'Premium'
    },
    fish: {
      title: 'Peces',
      description: 'Estándar - Usuarios con $10,000 - $100,000 en depósitos',
      color: '#52c41a',
      gradient: 'linear-gradient(135deg, #95de64 0%, #52c41a 100%)',
      icon: RiseOutlined,
      level: 'Estándar'
    },
    inactive: {
      title: 'Inactivos',
      description: 'Básico - Usuarios con menos de $10,000 en depósitos',
      color: '#bfbfbf',
      gradient: 'linear-gradient(135deg, #d9d9d9 0%, #bfbfbf 100%)',
      icon: ClockCircleOutlined,
      level: 'Básico'
    }
  };
  return categoryMap[category as keyof typeof categoryMap] || categoryMap.total;
};

export const UserDetailsDrawer: React.FC<UserDetailsDrawerProps> = ({
  visible,
  onClose,
  category,
  categoryData,
  onLoadDetails,
  theme = 'light'
}) => {
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<UserCategoryDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [userOperations, setUserOperations] = useState<{[userId: string]: any[]}>({});
  const [loadingOperations, setLoadingOperations] = useState<{[userId: string]: boolean}>({});
  const [userOperationsPagination, setUserOperationsPagination] = useState<{[userId: string]: {current: number, total: number, pageSize: number}}>({});
  const pageSize = 20;

  const categoryInfo = category ? getCategoryInfo(category) : null;

  useEffect(() => {
    updateStyles(theme);
  }, [theme]);

  useEffect(() => {
    if (visible && category) {
      loadData(1);
    }
  }, [visible, category]);

  useEffect(() => {
    if (details?.users?.length > 0) {
    }
  }, [details]);

  const loadData = async (page: number) => {
    if (!category) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const offset = (page - 1) * pageSize;
      const result = await onLoadDetails(category, pageSize, offset);
      setDetails(result);
      setCurrentPage(page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar detalles');
      setDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    loadData(page);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: any) => {
    if (!date || date === null || date === undefined) return '-';
    
    if (typeof date === 'object' && Object.keys(date).length === 0) return '-';
    
    if (typeof date === 'string' && date.trim() === '') return '-';
    
    try {
      const parsedDate = dayjs(date);
      if (!parsedDate.isValid()) return '-';
      return parsedDate.format('DD/MM/YY');
    } catch {
      return '-';
    }
  };

  const getSegmentBadge = (totalDeposits: number) => {
    if (totalDeposits > 500000) return { text: 'Elite', color: '#722ed1' };
    if (totalDeposits > 100000) return { text: 'Premium', color: '#1890ff' };
    if (totalDeposits > 10000) return { text: 'Estándar', color: '#52c41a' };
    return { text: 'Básico', color: '#bfbfbf' };
  };

  const columns = [
    {
      title: (
        <Space size={4}>
          <UserOutlined style={{ fontSize: '12px' }} />
          <span style={{ fontSize: '12px', fontWeight: 600 }}>Usuario</span>
        </Space>
      ),
      dataIndex: 'username',
      key: 'username',
      width: 160,
      render: (username: string, record: any) => {
        const segment = getSegmentBadge(record.totalDeposits);
        return (
          <div style={{ padding: '2px 0' }}>
            <div style={{ marginBottom: '2px' }}>
              <Text strong style={{ 
                fontSize: '13px', 
                color: theme === 'dark' ? '#fff' : '#1f1f1f'
              }}>
                {username}
              </Text>
            </div>
            <div style={{ marginBottom: '2px' }}>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {record.accountName}
              </Text>
            </div>
            <Badge 
              count={segment.text} 
              style={{ 
                backgroundColor: segment.color,
                fontSize: '9px',
                height: '14px',
                lineHeight: '12px',
                padding: '0 4px'
              }} 
            />
          </div>
        );
      }
    },
    {
      title: (
        <Space size={4}>
          <span style={{ fontSize: '12px', fontWeight: 600 }}>Código</span>
        </Space>
      ),
      dataIndex: 'code',
      key: 'code',
      width: 100,
      render: (code: string | null) => (
        <Text style={{
          fontSize: '11px',
          color: code ? '#52c41a' : '#8c8c8c',
          fontWeight: code ? 600 : 400
        }}>
          {code || 'Sin código'}
        </Text>
      )
    },
    {
      title: (
        <Space size={4}>
          <DollarOutlined style={{ fontSize: '12px' }} />
          <span style={{ fontSize: '12px', fontWeight: 600 }}>Depósitos</span>
        </Space>
      ),
      dataIndex: 'totalDeposits',
      key: 'totalDeposits',
      width: 120,
      render: (amount: number) => {
        const segment = getSegmentBadge(amount);
        return (
          <Text strong style={{ 
            fontSize: '12px', 
            color: segment.color
          }}>
            ${formatCurrency(amount)}
          </Text>
        );
      },
      sorter: true
    },
    {
      title: (
        <Space size={4}>
          <RiseOutlined style={{ fontSize: '12px' }} />
          <span style={{ fontSize: '12px', fontWeight: 600 }}>Ops</span>
        </Space>
      ),
      dataIndex: 'operationCount',
      key: 'operationCount',
      width: 70,
      render: (count: number) => (
        <div style={{ textAlign: 'center' }}>
          <Text strong style={{ 
            fontSize: '12px', 
            color: '#1890ff'
          }}>
            {count}
          </Text>
        </div>
      )
    },
    {
      title: (
        <span style={{ fontSize: '12px', fontWeight: 600 }}>Primera</span>
      ),
      dataIndex: 'firstOperationDate', 
      key: 'firstOperationDate',
      width: 80,
      render: (date: any, record: any) => {
        const possibleDates = [
          date,
          record.firstOperationDate,
          record.firstOperation,
          record.firstOperationAt,
          record.createdAt
        ];
        
        const dateValue = possibleDates.find(d => {
          if (!d || d === null || d === undefined) return false;
          if (typeof d === 'object' && Object.keys(d).length === 0) return false;
          if (typeof d === 'string' && d.trim() === '') return false;
          return true;
        });
        
        return (
          <Text style={{ fontSize: '11px', color: '#13c2c2' }}>
            {formatDate(dateValue)}
          </Text>
        );
      }
    },
    {
      title: (
        <span style={{ fontSize: '12px', fontWeight: 600 }}>Última</span>
      ),
      dataIndex: 'lastOperationDate',
      key: 'lastOperationDate', 
      width: 80,
      render: (date: any, record: any) => {
        const possibleDates = [
          date,
          record.lastOperationDate,
          record.lastOperation,
          record.lastOperationAt,
          record.updatedAt
        ];
        
        const dateValue = possibleDates.find(d => {
          if (!d || d === null || d === undefined) return false;
          if (typeof d === 'object' && Object.keys(d).length === 0) return false;
          if (typeof d === 'string' && d.trim() === '') return false;
          return true;
        });
        
        return (
          <Text style={{ fontSize: '11px', color: '#fa8c16' }}>
            {formatDate(dateValue)}
          </Text>
        );
      }
    },
    {
      title: '',
      key: 'operations',
      width: 50,
      render: (_, record: any) => (
        <Button 
          type="text"
          icon={<EyeOutlined />}
          onClick={() => handleShowOperations(record.userId)}
          size="small"
          style={{
            fontSize: '12px',
            height: '24px',
            width: '24px',
            padding: 0,
            color: theme === 'dark' ? '#1890ff' : '#1890ff'
          }}
        />
      )
    }
  ];

  const handleShowOperations = async (userId: string, page: number = 1) => {
    if (userOperations[userId] && page === 1) {
      const newOperations = { ...userOperations };
      const newPagination = { ...userOperationsPagination };
      delete newOperations[userId];
      delete newPagination[userId];
      setUserOperations(newOperations);
      setUserOperationsPagination(newPagination);
      return;
    }

    setLoadingOperations(prev => ({ ...prev, [userId]: true }));
    
    try {
      
      const limit = 50;
      const offset = (page - 1) * limit;
      
      const operationsData = await AnalyticsService.getUserOperations(userId, limit, offset);
      
      
      setUserOperations(prev => ({ ...prev, [userId]: operationsData.operations }));
      setUserOperationsPagination(prev => ({ 
        ...prev, 
        [userId]: {
          current: page,
          total: operationsData.totalCount,
          pageSize: limit
        }
      }));
    } catch (error) {
      console.error('❌ [UserDetailsDrawer] Error loading operations:', error);
      setUserOperations(prev => ({ ...prev, [userId]: [] }));
      setUserOperationsPagination(prev => ({ 
        ...prev, 
        [userId]: {
          current: 1,
          total: 0,
          pageSize: 50
        }
      }));
    } finally {
      setLoadingOperations(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleClose = () => {
    setDetails(null);
    setCurrentPage(1);
    setError(null);
    setUserOperations({});
    setLoadingOperations({});
    setUserOperationsPagination({});
    onClose();
  };

  return (
    <Drawer
      title={null}
      placement="right"
      onClose={handleClose}
      open={visible}
      width={720}
      closeIcon={false}
      styles={{
        header: {
          display: 'none'
        },
        body: {
          background: theme === 'dark' ? '#141414' : '#f8f9fa',
          padding: 0
        }
      }}
    >
      {/* Header personalizado */}
      <div style={{
        background: categoryInfo?.gradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px 24px',
        position: 'relative'
      }}>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            color: 'white',
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: '6px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        />
        
        <Space align="center" size={12}>
          {categoryInfo && (
            <>
              <div style={{
                background: 'rgba(255,255,255,0.2)',
                padding: '8px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px'
              }}>
                {React.createElement(categoryInfo.icon, { 
                  style: { fontSize: '18px', color: 'white' } 
                })}
              </div>
              <div>
                <Title level={4} style={{ 
                  color: 'white', 
                  fontSize: '18px', 
                  fontWeight: 600,
                  margin: 0,
                  marginBottom: '4px'
                }}>
                  {categoryInfo.title}
                </Title>
                <Space size={8}>
                  <Badge 
                    count={categoryInfo.level}
                    style={{ 
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontSize: '11px',
                      border: '1px solid rgba(255,255,255,0.3)',
                      height: '18px',
                      lineHeight: '16px',
                      padding: '0 8px'
                    }} 
                  />
                  <Badge 
                    count={`${categoryData?.count || 0} usuarios`}
                    style={{ 
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      color: categoryInfo.color,
                      fontSize: '11px',
                      fontWeight: 600,
                      height: '18px',
                      lineHeight: '16px',
                      padding: '0 8px'
                    }} 
                  />
                </Space>
              </div>
            </>
          )}
        </Space>
      </div>

      {/* Contenido principal */}
      <div style={{ padding: '20px 24px' }}>
        {/* Stats Card */}
        {categoryInfo && (
          <div style={{ 
            marginBottom: 16,
            background: theme === 'dark' ? '#1f1f1f' : '#ffffff',
            borderRadius: '8px',
            border: `1px solid ${theme === 'dark' ? '#303030' : '#e8e8e8'}`,
            overflow: 'hidden',
            boxShadow: theme === 'dark'
              ? '0 2px 8px rgba(0,0,0,0.15)'
              : '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            <div style={{
              background: theme === 'dark' ? '#262626' : '#fafafa',
              padding: '12px 16px',
              borderBottom: `1px solid ${theme === 'dark' ? '#303030' : '#e8e8e8'}`
            }}>
              <Text type="secondary" style={{ 
                fontSize: '12px',
                display: 'block'
              }}>
                {categoryInfo.description}
              </Text>
            </div>
            <div style={{ padding: '16px' }}>
              <Row gutter={16}>
                <Col span={8}>
                  <div style={{ textAlign: 'center' }}>
                    <Text strong style={{ 
                      fontSize: '20px', 
                      color: categoryInfo.color, 
                      display: 'block' 
                    }}>
                      {(categoryData?.count || 0).toLocaleString()}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Usuarios
                    </Text>
                  </div>
                </Col>
                {categoryData?.totalValue !== undefined && (
                  <Col span={8}>
                    <div style={{ textAlign: 'center' }}>
                      <Text strong style={{ 
                        fontSize: '16px', 
                        color: '#52c41a', 
                        display: 'block' 
                      }}>
                        ${formatCurrency(categoryData.totalValue)}
                      </Text>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Total Depósitos
                      </Text>
                    </div>
                  </Col>
                )}
                <Col span={8}>
                  <div style={{ textAlign: 'center' }}>
                    <Text strong style={{ 
                      fontSize: '20px', 
                      color: '#1890ff' 
                    }}>
                      {(categoryData?.percentage || 0).toFixed(1)}%
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                      Del Total
                    </Text>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        )}

        {error && (
          <Alert
            message="Error al cargar detalles"
            description={error}
            type="error"
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}

        <Spin spinning={loading}>
          {details && (
            <div style={{
              background: theme === 'dark' ? '#1f1f1f' : '#ffffff',
              borderRadius: '8px',
              border: `1px solid ${theme === 'dark' ? '#303030' : '#e8e8e8'}`,
              overflow: 'hidden',
              boxShadow: theme === 'dark'
                ? '0 2px 8px rgba(0,0,0,0.15)'
                : '0 2px 8px rgba(0,0,0,0.06)'
            }}>
              {/* Header de la tabla */}
              <div style={{
                background: theme === 'dark' ? '#262626' : '#fafafa',
                padding: '12px 16px',
                borderBottom: `1px solid ${theme === 'dark' ? '#303030' : '#e8e8e8'}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Text style={{ 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: theme === 'dark' ? '#fff' : '#1f1f1f' 
                }}>
                  Listado de Usuarios ({details.users.length} de {details.totalCount})
                </Text>
                
                <Pagination
                  current={currentPage}
                  total={details.totalCount}
                  pageSize={pageSize}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  showQuickJumper={false}
                  size="small"
                  showTotal={(total, range) => `${range[0]}-${range[1]} de ${total}`}
                />
              </div>
              
              {/* Tabla */}
              <Table
                columns={columns}
                dataSource={details.users}
                rowKey="userId"
                pagination={false}
                size="small"
                scroll={{ x: 650, y: 500 }}
                style={{
                  background: 'transparent'
                }}
                rowClassName={(_, index) => 
                  index % 2 === 0 ? 'row-even' : 'row-odd'
                }
                expandable={{
                  expandedRowRender: (record) => {
                    const operations = userOperations[record.userId];
                    const isLoadingOps = loadingOperations[record.userId];
                    const pagination = userOperationsPagination[record.userId];
                    
                    if (isLoadingOps) {
                      return (
                        <div style={{ 
                          textAlign: 'center', 
                          padding: '20px',
                          background: theme === 'dark' ? '#141414' : '#f8f9fa'
                        }}>
                          <Spin size="default" />
                          <Text style={{ 
                            display: 'block', 
                            marginTop: '8px', 
                            color: theme === 'dark' ? '#d9d9d9' : '#666',
                            fontSize: '12px'
                          }}>
                            Cargando historial...
                          </Text>
                        </div>
                      );
                    }
                    
                    if (!operations || operations.length === 0) {
                      return (
                        <div style={{ 
                          textAlign: 'center', 
                          padding: '20px',
                          background: theme === 'dark' ? '#141414' : '#f8f9fa'
                        }}>
                          <EyeOutlined style={{ 
                            fontSize: '24px', 
                            color: theme === 'dark' ? '#8c8c8c' : '#bfbfbf', 
                            marginBottom: '8px' 
                          }} />
                          <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                            No hay operaciones para mostrar
                          </Text>
                        </div>
                      );
                    }

                    const segment = getSegmentBadge(record.totalDeposits);

                    return (
                      <div style={{ 
                        background: theme === 'dark' ? '#141414' : '#f8f9fa',
                        padding: '16px'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          marginBottom: 12,
                          paddingBottom: '8px',
                          borderBottom: `1px solid ${theme === 'dark' ? '#303030' : '#e8e8e8'}`
                        }}>
                          <Space>
                            <div style={{
                              background: segment.color,
                              width: '20px',
                              height: '20px',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <EyeOutlined style={{ color: 'white', fontSize: '10px' }} />
                            </div>
                            <div>
                              <Text strong style={{ 
                                margin: 0, 
                                color: theme === 'dark' ? '#fff' : segment.color,
                                fontSize: '13px'
                              }}>
                                Historial de {record.username}
                              </Text>
                              <Text type="secondary" style={{ 
                                fontSize: '11px', 
                                display: 'block' 
                              }}>
                                Usuario {segment.text}
                              </Text>
                            </div>
                          </Space>
                          {pagination && (
                            <Badge 
                              count={`${pagination.total} operaciones`}
                              style={{ 
                                backgroundColor: segment.color,
                                fontSize: '10px',
                                height: '18px',
                                lineHeight: '16px'
                              }}
                            />
                          )}
                        </div>
                        
                        <Table
                          size="small"
                          dataSource={operations}
                          rowKey="id"
                          pagination={pagination && pagination.total > pagination.pageSize ? {
                            current: pagination.current,
                            total: pagination.total,
                            pageSize: pagination.pageSize,
                            onChange: (page) => handleShowOperations(record.userId, page),
                            showSizeChanger: false,
                            showQuickJumper: false,
                            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
                            size: 'small'
                          } : false}
                          style={{ background: 'transparent' }}
                          columns={[
                            {
                              title: 'Tipo',
                              dataIndex: 'type',
                              key: 'type',
                              width: 80,
                              render: (type: string) => (
                                <Tag 
                                  color={type === 'DEPOSIT' ? 'success' : 'error'}
                                  style={{
                                    borderRadius: '4px',
                                    fontWeight: 500,
                                    fontSize: '10px',
                                    padding: '0 6px',
                                    height: '20px',
                                    lineHeight: '18px'
                                  }}
                                >
                                  {type === 'DEPOSIT' ? (
                                    <Space size={2}>
                                      <RiseOutlined style={{ fontSize: '8px' }} />
                                      <span>Depósito</span>
                                    </Space>
                                  ) : (
                                    <Space size={2}>
                                      <FallOutlined style={{ fontSize: '8px' }} />
                                      <span>Retiro</span>
                                    </Space>
                                  )}
                                </Tag>
                              )
                            },
                            {
                              title: 'Monto',
                              dataIndex: 'amount',
                              key: 'amount',
                              width: 120,
                              render: (amount: number, record: any) => (
                                <Text strong style={{ 
                                  color: record.type === 'DEPOSIT' ? '#52c41a' : '#f5222d',
                                  fontSize: '11px'
                                }}>
                                  ${formatCurrency(amount)}
                                </Text>
                              )
                            },
                            {
                              title: 'Fecha',
                              dataIndex: 'createdAt',
                              key: 'createdAt',
                              width: 100,
                              render: (date: Date | string | null) => (
                                <Space size={4}>
                                  <CalendarOutlined style={{ color: '#1890ff', fontSize: '10px' }} />
                                  <Text style={{ fontSize: '11px' }}>{formatDate(date)}</Text>
                                </Space>
                              )
                            },
                            {
                              title: 'Descripción',
                              dataIndex: 'description',
                              key: 'description',
                              render: (description: string) => (
                                <Text style={{ fontSize: '11px' }} type="secondary">
                                  {description || 'Sin descripción'}
                                </Text>
                              )
                            }
                          ]}
                        />
                      </div>
                    );
                  },
                  rowExpandable: (record) => !!userOperations[record.userId],
                  onExpand: (expanded, record) => {
                    if (expanded && !userOperations[record.userId]) {
                      handleShowOperations(record.userId);
                    }
                  },
                  expandIcon: ({ expanded, onExpand, record }) => 
                    userOperations[record.userId] ? (
                      <DownOutlined 
                        rotate={expanded ? 180 : 0} 
                        onClick={e => onExpand(record, e)} 
                        style={{ 
                          cursor: 'pointer',
                          color: '#1890ff',
                          fontSize: '10px'
                        }}
                      />
                    ) : null
                }}
              />
            </div>
          )}
        </Spin>
      </div>
    </Drawer>
  );
};

// Estilos CSS personalizados optimizados para el diseño profesional
const getCustomStyles = (theme: 'dark' | 'light') => `
  .row-even {
    background: ${theme === 'dark' ? '#1f1f1f' : '#ffffff'} !important;
  }
  
  .row-odd {
    background: ${theme === 'dark' ? '#262626' : '#fafafa'} !important;
  }
  
  .row-even:hover,
  .row-odd:hover {
    background: ${theme === 'dark' ? '#303030' : '#f0f9ff'} !important;
    transition: all 0.2s ease !important;
  }
  
  .ant-table-thead > tr > th {
    background: ${theme === 'dark' ? '#262626' : '#fafafa'} !important;
    border-bottom: 1px solid ${theme === 'dark' ? '#303030' : '#e8e8e8'} !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    padding: 8px 12px !important;
    color: ${theme === 'dark' ? '#fff' : '#1f1f1f'} !important;
    height: 36px !important;
  }
  
  .ant-table-tbody > tr > td {
    padding: 8px 12px !important;
    border-bottom: 1px solid ${theme === 'dark' ? '#303030' : '#f0f0f0'} !important;
    vertical-align: middle !important;
    height: 48px !important;
  }
  
  .ant-table-expanded-row > td {
    padding: 0 !important;
    background: transparent !important;
  }
  
  .ant-pagination-item {
    border-radius: 4px !important;
    font-weight: 500 !important;
    font-size: 12px !important;
    height: 28px !important;
    min-width: 28px !important;
    line-height: 26px !important;
  }
  
  .ant-pagination-prev,
  .ant-pagination-next,
  .ant-pagination-jump-prev,
  .ant-pagination-jump-next {
    font-size: 12px !important;
    height: 28px !important;
    min-width: 28px !important;
    line-height: 26px !important;
  }
  
  .ant-drawer-content {
    border-radius: 0 !important;
    overflow: hidden !important;
  }
  
  .ant-table-container {
    border-radius: 0 !important;
  }
  
  .ant-table {
    background: transparent !important;
  }
  
  .ant-spin-container {
    background: transparent !important;
  }
`;

// Actualizar estilos dinámicamente
const updateStyles = (theme: 'dark' | 'light') => {
  if (typeof document !== 'undefined') {
    const existingStyle = document.getElementById('user-drawer-styles');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    const styleElement = document.createElement('style');
    styleElement.id = 'user-drawer-styles';
    styleElement.textContent = getCustomStyles(theme);
    document.head.appendChild(styleElement);
  }
};